import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Typography } from '@mui/material';
import { LocationProvider } from './contexts/LocationContext';
import { QueueProvider } from './contexts/QueueContext';
import { SipNumberProvider } from './contexts/SipNumberContext';
import { UserProvider } from './contexts/UserContext';
import { ReportsProvider } from './contexts/ReportsContext';
import { AgentStatusProvider } from './contexts/AgentStatusContext';
import { QueueRoutingProvider } from './contexts/QueueRoutingContext';
import { CallRecordingProvider } from './contexts/CallRecordingContext';
import { MusicFileProvider } from './contexts/MusicFileContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import RealtimeNotifications from './components/RealtimeNotifications';
import WelcomeDashboard from './pages/WelcomeDashboard';
import Login from './pages/Login';
import AgentPanel from './pages/AgentPanel.freepbx';
import SupervisorPanel from './pages/SupervisorPanel';
import AdminDashboard from './pages/AdminDashboard';
import SipManagement from './pages/SipManagement';
import QueueManagement from './pages/QueueManagement';
import UserManagement from './pages/UserManagement';
import LocationManagement from './pages/LocationManagement';
import CallManagement from './pages/CallManagement';
import AgentStatusManagement from './pages/AgentStatusManagement';
import Dashboard from './pages/Dashboard';
import IvrManagement from './pages/IvrManagement';
import IvrBuilder from './pages/IvrBuilder';
import AdminOptions from './pages/AdminOptions';
import SystemSettings from './pages/SystemSettings';
import Reports from './pages/Reports';
import CustomerManagement from './pages/CustomerManagement';
import WebhooksManagement from './pages/WebhooksManagement';
import StatusManagement from './pages/StatusManagement';
import SupervisorDashboard from './pages/SupervisorDashboard';
import CampaignManagement from './pages/CampaignManagement';
import DialerDashboard from './pages/DialerDashboard';
import Signup from './pages/Signup';
import theme from './theme';
import authService from './services/authService';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simple authentication check
    const token = localStorage.getItem('accessToken');
    setIsAuthenticated(!!token);
    setLoading(false);
  }, []);

  // Sekme kapatıldığında logout işlemi - şimdilik devre dışı
  // beforeunload her sayfa yenilemesinde de tetikleniyor, bu yüzden kullanıcıyı sürekli logout yapıyor
  // Alternatif: Backend'deki heartbeat timeout mekanizması kullanılıyor
  /*
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const logoutUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/logout`;
          
          fetch(logoutUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            keepalive: true,
            body: JSON.stringify({})
          });
        } catch (error) {
          console.error('Logout error:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  */

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Typography>Yükleniyor...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <UserProvider>
            <LocationProvider>
              <QueueProvider>
                <SipNumberProvider>
                  <ReportsProvider>
                    <AgentStatusProvider>
                      <QueueRoutingProvider>
                        <CallRecordingProvider>
                          <MusicFileProvider>
                            <RealtimeProvider>
                              {isAuthenticated ? (
                                <Routes>
                                  <Route path="/" element={<WelcomeDashboard />} />
                                  <Route path="/login" element={<Login />} />
                                  <Route path="/agent-panel" element={<AgentPanel />} />
                                  <Route path="/agent-panel-new" element={<AgentPanel />} />
                                  <Route path="/supervisor-panel" element={<SupervisorPanel />} />
                                  <Route path="/admin-dashboard" element={<AdminDashboard />} />
                                  <Route path="/sip-management" element={<SipManagement />} />
                                  <Route path="/queue-management" element={<QueueManagement />} />
                                  <Route path="/user-management" element={<UserManagement />} />
                                  <Route path="/location-management" element={<LocationManagement />} />
                                  <Route path="/ivr-management" element={<IvrManagement />} />
                                  <Route path="/ivr-builder" element={<IvrBuilder />} />
                                  <Route path="/call-management" element={<CallManagement />} />
                                  <Route path="/agent-status-management" element={<AgentStatusManagement />} />
                                  <Route path="/dashboard" element={<Dashboard />} />
                                  <Route path="/admin-options" element={<AdminOptions />} />
                                  <Route path="/admin/settings" element={<SystemSettings />} />
                                  <Route path="/admin/reports" element={<Reports />} />
                                  <Route path="/admin/customers" element={<CustomerManagement />} />
                                  <Route path="/admin/webhooks" element={<WebhooksManagement />} />
                                  <Route path="/statuses" element={<StatusManagement />} />
                                  <Route path="/supervisor-dashboard" element={<SupervisorDashboard />} />
                                  <Route path="/campaigns" element={<CampaignManagement />} />
                                  <Route path="/dialer-dashboard" element={<DialerDashboard />} />
                                </Routes>
                              ) : (
                                <Routes>
                                  <Route path="/signup" element={<Signup />} />
                                  <Route path="*" element={<Login />} />
                                </Routes>
                              )}
                              <RealtimeNotifications />
                            </RealtimeProvider>
                          </MusicFileProvider>
                        </CallRecordingProvider>
                      </QueueRoutingProvider>
                    </AgentStatusProvider>
                  </ReportsProvider>
                </SipNumberProvider>
              </QueueProvider>
            </LocationProvider>
          </UserProvider>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;